import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import VerifierForm from './VerifierForm';
import VerifierSuggestions from './VerifierSuggestions';
import { rkeyFromUri, verificationRkey, chunkArray, assessProfile } from '../../lib/verifications';

// Chunk size for bulk writes via com.atproto.repo.applyWrites (P1)
const BULK_WRITE_CHUNK = 200;

// Chunk size for actor.getProfiles lookups during staleness checks (P4)
const PROFILE_CHECK_CHUNK = 25;

// Shared pagination helper (P3): follows `cursor` until it is absent.
// `fn` is (params) => Promise<{ data }> for an agent call.
// `onPage(loadedCount)` is called after each page so the UI can show progress.
async function paginate(fn, params, onPage) {
  const all = [];
  let cursor;
  const p = { ...params };

  do {
    if (cursor) p.cursor = cursor;
    const res = await fn(p);
    const data = res?.data ?? res;
    const listKey = Object.keys(data || {}).find(k => Array.isArray(data[k]));
    const items = listKey ? data[listKey] : [];
    all.push(...items);

    if (onPage) onPage(all.length);
    cursor = data?.cursor;
  } while (cursor);

  return all;
}

// Derive an rkey from a record uri: at://did/collection/rkey
// (rkeyFromUri / verificationRkey live in src/lib/verifications.js — tested.)

export default function Verifier() {
  const { agent, session, loading: isAuthLoading, error: authError } = useAuth();

  const [verifications, setVerifications] = useState([]);
  const [isLoadingVerifications, setIsLoadingVerifications] = useState(false);

  const [userLists, setUserLists] = useState([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);

  const [mode, setMode] = useState('account');
  const [targetHandle, setTargetHandle] = useState('');
  const [selectedListUri, setSelectedListUri] = useState('');

  const [bulkProgress, setBulkProgress] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [isVerifying, setIsVerifying] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);

  // P2: load-once duplicate index. Map<subjectDid, {uri, rkey, handle, displayName, createdAt}>
  const verificationIndexRef = useRef(new Map());

  // P4: staleness check + re-issue
  const [isCheckingChanges, setIsCheckingChanges] = useState(false);
  const [isReissuing, setIsReissuing] = useState(false);

  // P7: import/export
  const importFileRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);

  // --- Agent comes from UseAuth (AuthContext builds it from the session) ---

  // --- Data fetching (P2/P3: full cursor-loop, build the duplicate index) ---
  const loadVerifications = useCallback(async () => {
    if (!agent || !session) return;
    setIsLoadingVerifications(true);

    try {
      const records = await paginate(
        (p) => agent.api.com.atproto.repo.listRecords(p),
        { repo: session.did, collection: 'app.bsky.graph.verification', limit: 100 }
      );

      const map = new Map();

      const formatted = records.map(r => {
        const value = r.value || {};
        const uri = r.uri;
        map.set(value.subject, {
          uri,
          rkey: rkeyFromUri(uri),
          handle: value.handle,
          displayName: value.displayName,
          createdAt: value.createdAt,
        });

        return {
          uri, cid: r.cid,
          handle: value.handle, displayName: value.displayName,
          subject: value.subject, createdAt: value.createdAt,
          stale: false, gone: false,
        };
      });

      verificationIndexRef.current = map;
      setVerifications(formatted);
    } catch (error) {
      setStatusMessage(`Failed to load verifications: ${error.message || 'Unknown error'}`);
      setStatusType('error');
    } finally {
      setIsLoadingVerifications(false);
    }
  }, [agent, session]);

  const fetchUserLists = useCallback(async () => {
    if (!agent || !session) return;
    setIsLoadingLists(true);

    try {
      const lists = await paginate(
        (p) => agent.api.app.bsky.graph.getLists(p),
        { actor: session.did, limit: 100 }
      );

      setUserLists(lists.filter(l => l.purpose === 'app.bsky.graph.defs#curatelist'));
    } finally {
      setIsLoadingLists(false);
    }
  }, [agent, session]);

  useEffect(() => {
    if (agent && session) { loadVerifications(); fetchUserLists(); }
  }, [agent, session, loadVerifications, fetchUserLists]);

  // --- Derived data (memoized) ---
  const verifiedByHandle = useMemo(() => {
    const map = {};
    verifications.forEach(v => { map[v.handle.toLowerCase()] = v; });

    return map;
  }, [verifications]);

  // --- Typeahead ---
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) { setSuggestions([]); setShowSuggestions(false);

 return; }

    setIsLoadingSuggestions(true);
    setShowSuggestions(true);

    try {
      const res = await agent.api.app.bsky.actor.searchActorsTypeahead({
        q: query,
        limit: 10,
      });

      setSuggestions(res.data.actors || []);
    } catch {
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [agent]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setTargetHandle(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.trim() === '') { setSuggestions([]); setShowSuggestions(false);

 return; }

    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSuggestionSelect = (handle) => { setTargetHandle(handle); setShowSuggestions(false); setSuggestions([]); };

  // --- Verification ---
  // Writes one record idempotently (deterministic rkey derived from the
  // subject DID) and updates the local duplicate index immediately (P2).
  const writeVerification = useCallback(async (did, handle, displayName) => {
    const record = { $type: 'app.bsky.graph.verification', subject: did, handle, displayName, createdAt: new Date().toISOString() };

    const res = await agent.api.com.atproto.repo.putRecord({
      repo: session.did,
      collection: 'app.bsky.graph.verification',
      rkey: verificationRkey(did),
      record,
    });

    if (res?.data?.uri) {
      verificationIndexRef.current.set(did, {
        uri: res.data.uri, rkey: rkeyFromUri(res.data.uri),
        handle, displayName, createdAt: record.createdAt,
      });
    }

    return res;
  }, [agent, session]);

  const handleVerifyAccount = async (e) => {
    e.preventDefault();

    if (!agent || !session || !targetHandle) return;
    setIsVerifying(true);

    try {
      const profileRes = await agent.api.app.bsky.actor.getProfile({ actor: targetHandle });
      const targetDid = profileRes.data.did;
      const targetDisplayName = profileRes.data.displayName || profileRes.data.handle;

      // P2: check duplicates against the load-once index
      if (verificationIndexRef.current.has(targetDid)) {
        const existing = verificationIndexRef.current.get(targetDid);
        setStatusMessage(`@${targetHandle} is already verified (verified ${new Date(existing.createdAt).toLocaleString()}). See your verifications below.`);
        setStatusType('success');

        return;
      }

      await writeVerification(targetDid, targetHandle, targetDisplayName);
      const postText = `I just verified @${targetHandle} using Bluesky's verification system.`;
      const intentUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(postText)}`;
      setStatusMessage(
        <>Successfully verified {targetHandle}!{' '}
          <a href={intentUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--success-text)', fontWeight: 600, textDecoration: 'underline' }}>
            Post on Bluesky to let them know?
          </a></>
      );
      setStatusType('success');
      setTargetHandle('');
      loadVerifications();
    } catch (error) {
      setStatusMessage(`Verification failed: ${error.message || 'Unknown error'}`);
      setStatusType('error');
    } finally {
      setIsVerifying(false);
    }
  };

  // P1: bulk-verify via com.atproto.repo.applyWrites in chunks of 200 (P3: paginate full read)
  const handleVerifyList = async (e) => {
    e.preventDefault();

    if (!agent || !session || !selectedListUri) return;
    setIsVerifying(true);
    setBulkProgress(null);

    try {
      setStatusMessage('Fetching list members...');
      setStatusType('success');

      const members = await paginate(
        (p) => agent.api.app.bsky.graph.getList(p),
        { list: selectedListUri, limit: 100 },
        (loaded) => setStatusMessage(`Loaded ${loaded} members...`)
      );

      if (!members.length) { setStatusMessage('This list has no members.'); setStatusType('success');

 return; }

      // P1: build the full pending array first (after P2 dedupe)
      const pending = [];
      let skipped = 0;

      for (const m of members) {
        if (verificationIndexRef.current.has(m.did)) { skipped++; continue; }

        pending.push({
          $type: 'app.bsky.graph.verification',
          subject: m.did, handle: m.handle, displayName: m.displayName || m.handle,
          createdAt: new Date().toISOString(),
        });
      }

      // P1: submit via applyWrites in chunks of 200
      let verified = 0, failed = 0;
      const failedHandles = [];
      // P1: submit via applyWrites in chunks of 200 (chunkArray: tested lib helper).
      // Sequential + awaited: the summary below must run after the last chunk.
      const chunks = chunkArray(pending, BULK_WRITE_CHUNK);

      for (const [chunkIndex, chunk] of chunks.entries()) {
        const chunkNo = chunkIndex + 1;
        const totalChunks = chunks.length;
        setBulkProgress({ current: Math.min(chunkIndex * BULK_WRITE_CHUNK + chunk.length, pending.length), total: pending.length, currentHandle: `chunk ${chunkNo}/${totalChunks}` });

        try {
          const res = await agent.api.com.atproto.repo.applyWrites({
            repo: session.did,
            writes: chunk.map(r => ({ $type: 'com.atproto.repo.applyWrites#create', collection: 'app.bsky.graph.verification', value: r })),
          });

          // applyWrites returns one result per write in order; count successes/failures
          const results = res?.data?.results || [];

          for (let idx = 0; idx < results.length; idx++) {
            const item = results[idx];
            const r = chunk[idx];

            if (item?.create?.uri) {
              verified++;
              verificationIndexRef.current.set(r.subject, {
                uri: item.create.uri, rkey: rkeyFromUri(item.create.uri),
                handle: r.handle, displayName: r.displayName, createdAt: r.createdAt,
              });
            } else {
              failed++;
              failedHandles.push(`@${r.handle}`);
            }
          }
        } catch (error) {
          // Hard chunk failure: report the whole chunk's subjects as failed and continue
          failed += chunk.length;
          chunk.forEach(r => failedHandles.push(`@${r.handle}`));
          void error; // chunk-level failure is aggregated via failedHandles
        }
      }

      const listName = userLists.find(l => l.uri === selectedListUri)?.name || 'list';
      let msg = `Finished verifying "${listName}": ${verified} verified, ${skipped} already verified, ${failed} failed (${members.length} total).`;

      if (failedHandles.length) {
        msg += ` Failed: ${failedHandles.slice(0, 10).join(', ')}${failedHandles.length > 10 ? ` (+${failedHandles.length - 10} more)` : ''}.`;
      }

      setStatusMessage(msg);
      setStatusType(failed > 0 ? 'error' : 'success');
      setSelectedListUri('');
      loadVerifications();
    } catch (error) {
      setStatusMessage(`List verification failed: ${error.message || 'Unknown error'}`);
      setStatusType('error');
    } finally {
      setIsVerifying(false);
      setBulkProgress(null);
    }
  };

  const handleVerify = mode === 'account' ? handleVerifyAccount : handleVerifyList;

  const handleRevoke = async (verification) => {
    if (!agent || !session) return;
    setIsVerifying(true);

    try {
      await agent.api.com.atproto.repo.deleteRecord({
        repo: session.did, collection: 'app.bsky.graph.verification', rkey: rkeyFromUri(verification.uri),
      });
      // P2: update the local duplicate index immediately
      verificationIndexRef.current.delete(verification.subject);
      setStatusMessage(`Revoked verification for ${verification.handle}`);
      setStatusType('success');
      loadVerifications();
    } catch (error) {
      setStatusMessage(`Revocation failed: ${error.message || 'Unknown error'}`);
      setStatusType('error');
    } finally {
      setIsVerifying(false);
    }
  };

  // P4: staleness check — compare current profiles against the P2 index (chunked ×25)
  const checkForChanges = useCallback(async () => {
    if (!agent || !session) return;
    const index = verificationIndexRef.current;
    const subjects = Array.from(index.keys());

    if (!subjects.length) { setStatusMessage('You have not verified any accounts yet.'); setStatusType('success');

 return; }

    setIsCheckingChanges(true);
    const updated = {};
    const returned = new Set();

    for (let i = 0; i < subjects.length; i += PROFILE_CHECK_CHUNK) {
      const chunk = subjects.slice(i, i + PROFILE_CHECK_CHUNK);
      setStatusMessage(`Checking for changes... (${Math.min(i + chunk.length, subjects.length)}/${subjects.length})`);

      try {
        const res = await agent.api.app.bsky.actor.getProfiles({ actors: chunk });
        (res?.data?.profiles || []).forEach(p => {
          const stored = index.get(p.did);

          if (!stored) return;
          returned.add(p.did);
          updated[p.did] = assessProfile(stored, p);
        });
      } catch (error) {
        // Hard failure for this batch — conservatively mark the batch as gone so the user can review
        console.warn('getProfiles batch failed:', error.message);
        chunk.forEach(did => { if (!updated[did]) updated[did] = assessProfile(index.get(did), null); });
      }
    }

    // Any subject not returned by getProfiles no longer resolves → gone
    subjects.forEach(did => {
      if (!updated[did] && !returned.has(did)) updated[did] = assessProfile(index.get(did), null);
    });
    setVerifications(prev => prev.map(v => {
      const c = updated[v.subject];

      return c ? { ...v, stale: c.stale, gone: c.gone, currentHandle: c.currentHandle, currentDisplayName: c.currentDisplayName } : v;
    }));
    const staleCount = Object.values(updated).filter(u => u.stale).length;
    const goneCount = Object.values(updated).filter(u => u.gone).length;
    setStatusMessage(`Staleness check complete. ${staleCount} stale, ${goneCount} gone.`);
    setStatusType('success');
    setIsCheckingChanges(false);
  }, [agent, session]);

  // P4: re-issue — update a record in place via putRecord on the SAME rkey
  const reissueVerification = async (verification) => {
    if (!agent || !session) return;
    const stored = verificationIndexRef.current.get(verification.subject);
    const rkey = stored?.rkey || verification.rkey || rkeyFromUri(verification.uri);

    if (!rkey) { setStatusMessage(`Cannot re-issue: missing rkey for ${verification.handle}.`); setStatusType('error');

 return; }

    setIsReissuing(true);

    try {
      const record = {
        $type: 'app.bsky.graph.verification',
        subject: verification.subject,
        handle: verification.currentHandle || verification.handle,
        displayName: verification.currentDisplayName || verification.displayName,
        createdAt: new Date().toISOString(),
      };

      const res = await agent.api.com.atproto.repo.putRecord({ repo: session.did, collection: 'app.bsky.graph.verification', rkey, record });
      verificationIndexRef.current.set(verification.subject, {
        uri: res?.data?.uri || verification.uri, rkey,
        handle: record.handle, displayName: record.displayName, createdAt: record.createdAt,
      });
      setStatusMessage(`Re-issued verification for ${verification.handle}.`);
      setStatusType('success');
      loadVerifications();
    } catch (error) {
      setStatusMessage(`Re-issue failed for ${verification.handle}: ${error.message || 'Unknown error'}`);
      setStatusType('error');
    } finally {
      setIsReissuing(false);
    }
  };

  // P7: export all records from the P2 index as JSON
  const handleExport = () => {
    const index = verificationIndexRef.current;

    const records = Array.from(index.entries()).map(([subject, entry]) => ({
      subject, handle: entry.handle, displayName: entry.displayName, createdAt: entry.createdAt,
    }));

    if (!records.length) { setStatusMessage('Nothing to export — you have not verified any accounts.'); setStatusType('success');

 return; }

    const handle = session?.handle || 'account';
    const date = new Date().toISOString().slice(0, 10);
    const blob = new Blob([JSON.stringify(records, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `verifications-${handle}-${date}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setStatusMessage(`Exported ${records.length} verification record(s).`);
    setStatusType('success');
  };

  // P7: import JSON → dedupe against the P2 index → bulk-create via the P1 applyWrites path
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const data = JSON.parse(reader.result);
        const records = Array.isArray(data) ? data : (data.records || []);

        if (!Array.isArray(records) || !records.length) { setStatusMessage('Import: no verification records found in file.'); setStatusType('error');

 return; }

        const index = verificationIndexRef.current;
        const newRecords = [], skipped = [];
        records.forEach(r => {
          const subject = r.subject || r.did;

          if (!subject) return;

          if (index.has(subject)) { skipped.push(subject);

 return; }

          const handle = r.handle || '';
          newRecords.push({ $type: 'app.bsky.graph.verification', subject, handle, displayName: r.displayName || handle, createdAt: r.createdAt || new Date().toISOString() });
        });

        if (!newRecords.length) { setStatusMessage(`Import: 0 new records. All ${records.length} already verified (skipped).`); setStatusType('success');

 return; }

        if (!window.confirm(`Import ${newRecords.length} new verification record(s)? ${skipped.length} will be skipped (already verified).`)) {
          setStatusMessage('Import cancelled.'); setStatusType('success');

 return;
        }

        setIsImporting(true);
        let successCount = 0, failureCount = 0;
        const errors = [];
        // chunkArray: tested lib helper (chunk remainder edge covered in tests).
        // Sequential + awaited: the summary below must run after the last chunk.
        const importChunks = chunkArray(newRecords, BULK_WRITE_CHUNK);

        for (const [chunkIndex, chunk] of importChunks.entries()) {
          setStatusMessage(`Importing... (${Math.min(chunkIndex * BULK_WRITE_CHUNK + chunk.length, newRecords.length)}/${newRecords.length})`);

          try {
            const res = await agent.api.com.atproto.repo.applyWrites({
              repo: session.did,
              writes: chunk.map(r => ({ $type: 'com.atproto.repo.applyWrites#create', collection: 'app.bsky.graph.verification', value: r })),
            });

            const results = res?.data?.results || [];

            for (let idx = 0; idx < results.length; idx++) {
              const item = results[idx];
              const r = chunk[idx];

              if (item?.create?.uri) {
                successCount++;
                verificationIndexRef.current.set(r.subject, { uri: item.create.uri, rkey: rkeyFromUri(item.create.uri), handle: r.handle, displayName: r.displayName, createdAt: r.createdAt });
              } else {
                failureCount++;
                errors.push(`@${r.handle || r.subject}: ${item?.error?.message || item?.error || 'unknown error'}`);
              }
            }
          } catch (error) {
            failureCount += chunk.length;
            errors.push(...chunk.map(r => `@${r.handle || r.subject}: ${error.message || 'Unknown error'}`));
          }
        }

        if (failureCount) console.error('Import errors:', errors);
        setStatusMessage(`Import complete. New: ${successCount}, Failed: ${failureCount}. ${skipped.length} skipped (already verified).`);
        setStatusType(failureCount ? 'error' : 'success');
        loadVerifications();
        setIsImporting(false);
      } catch (err) {
        setStatusMessage(`Import failed: ${err.message || 'Invalid file'}`);
        setStatusType('error');
        setIsImporting(false);
      }
    };

    reader.readAsText(file);

    // reset input so the same file can be re-selected later
    if (importFileRef.current) importFileRef.current.value = '';
  };

  // --- Render ---
  if (isAuthLoading) return <p>Loading authentication...</p>;

  if (authError) return <p>Authentication Error: {authError}. <a href="/login">Please login</a>.</p>;

  const isDuplicateHandle = !!(targetHandle && verifiedByHandle[targetHandle.toLowerCase()]);

  const duplicateDate = isDuplicateHandle
    ? new Date(verifiedByHandle[targetHandle.toLowerCase()].createdAt).toLocaleString()
    : '';

  const anyInProgress = isVerifying || isLoadingVerifications || isLoadingLists || isCheckingChanges || isReissuing || isImporting;

  return (
    <div className="verifier-container">
      <VerifierForm
        mode={mode} setMode={setMode}
        targetHandle={targetHandle} onChangeHandle={handleInputChange}
        selectedListUri={selectedListUri} setSelectedListUri={setSelectedListUri}
        userLists={userLists} isLoadingLists={isLoadingLists}
        isVerifying={isVerifying}
        isDuplicateHandle={isDuplicateHandle} duplicateDate={duplicateDate}
        onSubmit={handleVerify}
      />

      {mode === 'account' && showSuggestions && (
        <VerifierSuggestions
          suggestions={suggestions}
          isLoading={isLoadingSuggestions}
          onSelect={handleSuggestionSelect}
        />
      )}

      {bulkProgress && (
        <div className="verifier-status-box verifier-status-box-success">
          <p>Verifying {bulkProgress.current} of {bulkProgress.total}: @{bulkProgress.currentHandle}</p>
        </div>
      )}

      {statusMessage && (
        <div className={`verifier-status-box verifier-status-box-${statusType}`}>
          <p>{statusMessage}</p>
        </div>
      )}

      <div className="verifier-section">
        <div className="verifier-list-header">
          <h2>Your Verifications</h2>
          {/* P4/P7: manage-view toolbar */}
          <div className="verifier-manage-toolbar">
            <button onClick={checkForChanges} disabled={anyInProgress} className="verifier-action-button">
              {isCheckingChanges ? 'Checking...' : 'Check for changes'}
            </button>
            <button onClick={handleExport} disabled={anyInProgress} className="verifier-action-button">
              Export JSON
            </button>
            <button onClick={() => importFileRef.current?.click()} disabled={anyInProgress} className="verifier-action-button">
              {isImporting ? 'Importing...' : 'Import JSON'}
            </button>
            <input ref={importFileRef} type="file" accept="application/json,.json" onChange={handleImportFile} className="verifier-import-input" />
          </div>
        </div>

        {isLoadingVerifications ? <p>Loading...</p>
          : verifications.length === 0 ? <p>You haven't verified any accounts.</p>
          : (
            <ul className="verifier-list">
              {verifications.map(v => (
                <li key={v.uri} className={`verifier-list-item ${v.gone ? 'verifier-list-item-invalid' : ''}`}>
                  <div className="verifier-list-item-content">
                    <a href={`https://bsky.app/profile/${v.handle}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', textDecoration: 'none' }}>
                      <span className="verifier-display-name">{v.displayName}</span>
                      <span className="verifier-list-item-handle">@{v.handle}</span>
                    </a>
                    {v.stale && !v.gone && <span className="verifier-stale-badge">Stale</span>}
                    {v.gone && <span className="verifier-gone-badge">Gone (no longer resolves)</span>}
                    {v.stale && (
                      <div className="verifier-stale-change">
                        {`@${v.handle}`} → {`@${v.currentHandle || '?'}`}
                        {v.currentDisplayName ? ` • ${v.displayName} → ${v.currentDisplayName}` : ''}
                      </div>
                    )}
                    <div className="verifier-list-item-date">Verified: {new Date(v.createdAt).toLocaleString()}</div>
                  </div>
                  <div className="verifier-list-item-actions">
                    {v.stale && !v.gone && (
                      <button onClick={() => reissueVerification(v)} disabled={isReissuing || isVerifying} className="verifier-reissue-button">
                        Re-issue
                      </button>
                    )}
                    <button onClick={() => handleRevoke(v)} disabled={isVerifying} className="verifier-revoke-button">
                      {v.gone ? 'Revoke (suggested)' : 'Revoke'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  );
}